import React, { useState, useEffect } from "react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheckCircle, FiXCircle, FiX, FiPlusCircle } from "react-icons/fi";

const AddUniversities = ({ theme }) => {
  const [residencyOptions, setResidencyOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [selectedResidencies, setSelectedResidencies] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [cityId, setCityId] = useState("");
  const [universityName, setUniversityName] = useState("");
  const [status, setStatus] = useState("active");

  const [loadingResidency, setLoadingResidency] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [toast, setToast] = useState({ type: "", message: "" });

  // ✅ Fetch residencies on mount
  useEffect(() => {
    const fetchResidencies = async () => {
      setLoadingResidency(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/catalog/residency-status`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch residencies");
        const data = await res.json();
        const formatted = data.map(({ id, residency }) => ({
          value: id,
          label: residency,
        }));
        setResidencyOptions(formatted);
      } catch (err) {
        showToast("error", err.message || "Error fetching residencies");
      } finally {
        setLoadingResidency(false);
      }
    };

    fetchResidencies();
  }, []);

  // ✅ Fetch all cities using your API
  const fetchAllCities = async () => {
    setLoadingCities(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/get-cities_1`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch cities");
      const data = await res.json();
      const formatted = data.data.map(({ id, city_name }) => ({
        id,
        label: city_name,
      }));
      setCityOptions(formatted);
    } catch (err) {
      showToast("error", err.message || "Error fetching cities");
    } finally {
      setLoadingCities(false);
    }
  };

  // ✅ Fetch cities by country (for international)
  const fetchCitiesByCountry = async (selectedCountryId) => {
    try {
      setLoadingCities(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/cities/${selectedCountryId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.cities)) {
        const formatted = data.cities.map(({ id, city_name }) => ({
          id,
          label: city_name,
        }));
        setCityOptions(formatted);
      } else {
        setCityOptions([]);
        showToast("error", "No cities found for this country");
      }
    } catch (err) {
      console.error("Fetch cities error:", err);
      showToast("error", "Failed to load cities");
    } finally {
      setLoadingCities(false);
    }
  };

  // ✅ Auto-hide toast after 5s
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ type: "", message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (type, message) => setToast({ type, message });

  // ✅ Handle residency changes
  useEffect(() => {
    const isInternational = selectedResidencies.some(
      (r) => r.label.toLowerCase() === "international"
    );

    if (isInternational) {
      fetchCountries();
      // Clear city options when switching to international
      setCityOptions([]);
      setCityId("");
    } else {
      setCountries([]);
      setCountryId("");
      // Fetch all cities for non-international cases
      fetchAllCities();
    }
  }, [selectedResidencies]);

  // ✅ Fetch countries
  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/form-options`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch countries");
      const data = await res.json();
      const formattedCountries = (data.countries || []).map(
        ({ id, nicename }) => ({
          id,
          label: nicename,
        })
      );
      setCountries(formattedCountries);
    } catch (err) {
      console.error(err);
      showToast("error", err.message || "Error loading countries");
    } finally {
      setLoadingCountries(false);
    }
  };

  // ✅ Handle country change for international
  const handleCountryChange = (selectedCountryId) => {
    setCountryId(selectedCountryId);
    if (selectedCountryId) {
      fetchCitiesByCountry(selectedCountryId);
    } else {
      setCityOptions([]);
      setCityId("");
    }
  };

  // ✅ Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isInternational = selectedResidencies.some(
      (r) => r.label.toLowerCase() === "international"
    );

    if (selectedResidencies.length === 0 || !universityName.trim()) {
      showToast("error", "Please fill all fields");
      return;
    }

    if (isInternational && (!countryId || !cityId)) {
      showToast("error", "Please select country and city");
      return;
    }

    if (!isInternational && !cityId) {
      showToast("error", "Please select a city");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id;

    const payload = {
      residencyId: selectedResidencies.map((r) => r.value),
      university: universityName.trim(),
      status: status === "active",
      createdBy: userId || null,
      cityId: Number(cityId),
    };

    try {
      setSubmitting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-university`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to add university");

      showToast("success", "University added successfully!");
      setSelectedResidencies([]);
      setCountryId("");
      setCityId("");
      setUniversityName("");
      setStatus("active");
      setCityOptions([]);
    } catch (err) {
      showToast("error", err.message || "Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white rounded-3xl shadow-xl p-8 max-w-2xl mx-auto mt-10">
      {/* ✅ Toast Notification */}
      <div className="fixed top-6 right-6 z-50">
        <AnimatePresence>
          {toast.message && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
              className={`flex items-center max-w-xs p-4 border rounded-lg shadow-lg space-x-3 overflow-hidden ${
                toast.type === "error"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "bg-green-50 border-green-300 text-green-700"
              }`}
            >
              <div className="shrink-0">
                {toast.type === "error" ? (
                  <FiXCircle size={20} />
                ) : (
                  <FiCheckCircle size={20} />
                )}
              </div>
              <div className="flex-1 text-sm leading-snug">{toast.message}</div>
              <button
                onClick={() => setToast({ type: "", message: "" })}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <FiX size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2"
      >
        <FiPlusCircle className="text-blue-600" size={24} />
        Add New University
      </motion.h2>

      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="space-y-6"
      >
        {/* Residency */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            Residency (Select one or more)
          </label>
          {loadingResidency ? (
            <p className="text-gray-500">Loading residency options...</p>
          ) : (
            <Select
              isMulti
              options={residencyOptions}
              value={selectedResidencies}
              onChange={setSelectedResidencies}
              className="text-gray-800"
              placeholder="Select residency"
            />
          )}
        </div>

        {/* Country Dropdown (only if International) */}
        {selectedResidencies.some(
          (r) => r.label.toLowerCase() === "international"
        ) && (
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Country
            </label>
            {loadingCountries ? (
              <p className="text-gray-500">Loading countries...</p>
            ) : (
              <select
                value={countryId}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-5 py-3 text-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Country</option>
                {countries.map(({ id, label }) => (
                  <option key={id} value={id}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* City Dropdown */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            City {selectedResidencies.some(r => r.label.toLowerCase() === "international") ? "(International)" : "(Local)"}
          </label>
          {loadingCities ? (
            <p className="text-gray-500">Loading cities...</p>
          ) : (
            <select
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-5 py-3 text-lg focus:ring-2 focus:ring-blue-500"
              disabled={selectedResidencies.some(r => r.label.toLowerCase() === "international") && !countryId}
            >
              <option value="">Select City</option>
              {cityOptions.map(({ id, label }) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          )}
          {selectedResidencies.some(r => r.label.toLowerCase() === "international") && !countryId && (
            <p className="text-sm text-gray-500 mt-1">Please select a country first</p>
          )}
        </div>

        {/* University Name */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            University Name
          </label>
          <input
            type="text"
            value={universityName}
            onChange={(e) => setUniversityName(e.target.value)}
            placeholder="Enter university name"
            required
            className="w-full border border-gray-300 rounded-lg px-5 py-3 text-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Status */}
        <div className="flex space-x-4">
          {["active", "inactive"].map((option) => (
            <label
              key={option}
              className={`flex-1 cursor-pointer rounded-lg px-4 py-2 text-center font-medium border transition-colors ${
                status === option
                  ? option === "active"
                    ? "bg-green-100 border-green-300 text-green-800"
                    : "bg-red-100 border-red-300 text-red-800"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="status"
                value={option}
                checked={status === option}
                onChange={() => setStatus(option)}
                className="hidden"
              />
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </label>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Adding..." : "Add University"}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default AddUniversities;